/**
 * Toggle looping order/message alert sound (popups still show when off).
 */

import { HiVolumeOff, HiVolumeUp } from 'react-icons/hi';
import { useAdminAlertSound } from '../hooks/useAdminAlertSound';

interface AdminAlertSoundToggleProps {
  /** Unlock browser audio on first tap (Orders page). */
  onBeforeToggle?: () => void;
  /** Show "Test sound" beside the volume control. */
  showTestButton?: boolean;
  onTestSound?: () => void;
  /** Show short text label (header bar). */
  showLabel?: boolean;
}

export default function AdminAlertSoundToggle({
  onBeforeToggle,
  showTestButton = false,
  onTestSound,
  showLabel = false,
}: AdminAlertSoundToggleProps) {
  const { soundEnabled, toggleSound } = useAdminAlertSound();

  const handleToggle = () => {
    onBeforeToggle?.();
    toggleSound();
  };

  const title = soundEnabled
    ? 'Looping alert sound ON — repeats until you dismiss the popup'
    : 'Looping alert sound OFF — popups and browser alerts still work';

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleToggle}
        className={`p-2 rounded-lg border transition-colors ${
          soundEnabled
            ? 'bg-purple-100 border-purple-300 text-purple-600'
            : 'bg-gray-100 border-gray-300 text-gray-400'
        }`}
        title={title}
        aria-pressed={soundEnabled}
        aria-label={soundEnabled ? 'Turn looping alert sound off' : 'Turn looping alert sound on'}
      >
        {soundEnabled ? <HiVolumeUp className="w-5 h-5" /> : <HiVolumeOff className="w-5 h-5" />}
      </button>
      {showLabel && (
        <span className="text-xs text-gray-500 hidden md:inline max-w-[11rem] leading-tight">
          {soundEnabled ? 'Loop sound on' : 'Loop sound off'}
        </span>
      )}
      {showTestButton && onTestSound && (
        <button
          type="button"
          onClick={onTestSound}
          className="p-2 rounded-lg border border-gray-300 bg-gray-50 text-gray-600 hover:bg-gray-100 text-sm"
          title="Play one sample alarm (does not loop)"
        >
          Test sound
        </button>
      )}
    </div>
  );
}
